import React, { useState } from "react";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import FormControl from "@mui/material/FormControl";
import Select, { SelectChangeEvent } from "@mui/material/Select";
import Checkbox from "@mui/material/Checkbox";
import ListItemText from "@mui/material/ListItemText";
import OutlinedInput from "@mui/material/OutlinedInput";
import Button from "@mui/material/Button";
import { useWithRecommendations } from "../../pages/RecommenderPage/use-with-recommendations";

export const DEGREE_OPTIONS = ["Masters", "Doctorate"];

export const SUBFIELD_OPTIONS = [
  "Brain Science and Cognitive Psychology",
  "Climate and Environmental Psychology",
  "Clinical Psychology",
  "Counseling Psychology",
  "Developmental Psychology",
  "Educational/School Psychology",
  "Experimental Psychology",
  "Forensic and Public Service Psychology",
  "Health Psychology",
  "Human Factors and Engineering Psychology",
  "Industrial and Organizational Psychology",
  "Quantitative Psychology",
  "Rehabilitation Psychology",
  "Social Psychology",
  "Sport and Performance Psychology",
];

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

interface FilterDropdownsProps {
  showDegree?: boolean;
  showSubfield?: boolean;
  onSubfieldsChange?: (subfields: string[]) => void;
  onDegreesChange?: (degrees: string[]) => void;
}

const FilterDropdowns: React.FC<FilterDropdownsProps> = ({
  showDegree = false,
  showSubfield = false,
  onSubfieldsChange,
  onDegreesChange,
}) => {
  const { getRecommendations, notifyFiltersAdjusted } = useWithRecommendations();
  const recommendations = getRecommendations();

  const defaultSubfields =
    !recommendations?.recommendedFilters.adjusted &&
    recommendations?.recommendedFilters.subFields.length > 0
      ? recommendations?.recommendedFilters.subFields
      : [];

  const defaultDegrees =
    !recommendations?.recommendedFilters.adjusted &&
    recommendations?.recommendedFilters.degrees.length > 0
      ? recommendations?.recommendedFilters.degrees
      : [];

  const [selectedSubfields, setSelectedSubfields] = useState<string[]>(defaultSubfields);
  const [selectedDegrees, setSelectedDegrees] = useState<string[]>(defaultDegrees);

  const handleSubfieldChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    notifyFiltersAdjusted();
    const values = typeof value === "string" ? value.split(",") : value;
    setSelectedSubfields(values);
    onSubfieldsChange?.(values);
  };

  const handleDegreeChange = (event: SelectChangeEvent<string[]>) => {
    const {
      target: { value },
    } = event;
    notifyFiltersAdjusted();
    const values = typeof value === "string" ? value.split(",") : value;
    setSelectedDegrees(values);
    onDegreesChange?.(values);
  };

  const handleSeeAll = () => {
    notifyFiltersAdjusted();
    setSelectedSubfields([]);
    setSelectedDegrees([]);
    onSubfieldsChange?.([]);
    onDegreesChange?.([]);
  };

  const dropdownStyles = {
    m: 1,
    width: {
      xs: "150px",
      sm: "300px",
      md: "400px",
    },
    "& .MuiOutlinedInput-root": {
      height: { xs: "30px", sm: "50px" },
      fontSize: { xs: "11px", sm: "12px" },
    },
    "& .MuiInputLabel-root": {
      fontSize: { xs: "11px", sm: "12px" },
      transform: "translate(14px, 8px) scale(1)",
      "&.Mui-focused, &.MuiFormLabel-filled": {
        transform: "translate(14px, -9px) scale(0.75)",
      },
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: "4px",
    },
    "& .MuiSelect-select": {
      fontSize: { xs: "11px", sm: "12px" },
      lineHeight: "1.2",
    },
    "& .MuiMenuItem-root": {
      fontSize: { xs: "5px", md: "12px" },
      minHeight: "32px",
    },
    "& .MuiListItemText-primary": {
      fontSize: { xs: "11px", md: "30px" },
    },
    "& .MuiCheckbox-root": {
      padding: "4px",
    },
  };

  return (
    <div style={{ display: "flex", alignItems: "center", gap: "8px" }}>
      {showDegree && (
        <FormControl sx={dropdownStyles}>
          <InputLabel id="degree-dropdown-label">Filter by Degree Level</InputLabel>
          <Select
            labelId="degree-dropdown-label"
            id="degree-dropdown"
            multiple
            value={selectedDegrees}
            onChange={handleDegreeChange}
            input={<OutlinedInput label="Filter by Degree Level" />}
            renderValue={(selected) => selected.join(", ")}
            MenuProps={MenuProps}
          >
            {DEGREE_OPTIONS.map((degree) => (
              <MenuItem key={degree} value={degree}>
                <Checkbox checked={selectedDegrees.indexOf(degree) > -1} />
                <ListItemText primary={degree} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {showSubfield && (
        <FormControl sx={dropdownStyles}>
          <InputLabel id="subfield-dropdown-label">Filter by Subfield</InputLabel>
          <Select
            labelId="subfield-dropdown-label"
            id="subfield-dropdown"
            multiple
            value={selectedSubfields}
            onChange={handleSubfieldChange}
            input={<OutlinedInput label="Filter by Subfield" />}
            renderValue={(selected) => selected.join(", ")}
          >
            {SUBFIELD_OPTIONS.map((subfield) => (
              <MenuItem key={subfield} value={subfield}>
                <Checkbox checked={selectedSubfields.indexOf(subfield) > -1} />
                <ListItemText primary={subfield} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}

      {(showDegree || showSubfield) && (
        <Button
          disabled={selectedSubfields.length === 0 && selectedDegrees.length === 0}
          variant="outlined"
          onClick={handleSeeAll}
          sx={{
            m: 1,
            height: { xs: "30px", sm: "50px" },
            fontSize: { xs: "10px", sm: "12px" },
            minWidth: { xs: "100px", sm: "120px" },
            textTransform: "none",
          }}
        >
          Clear Filters
        </Button>
      )}
    </div>
  );
};

export default FilterDropdowns;
